package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.LockpickingStatus
import fr.manyverse.app.domain.repository.LockpickingRepository
import fr.manyverse.app.infrastructure.error.logInfrastructureFailure
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonPrimitive
import kotlin.time.Clock
import kotlin.time.Instant

private const val TAG = "LockpickingRepository"
private const val LOCKPICKINGS_TABLE = "game_table_lockpickings"

/**
 * Supabase implementation of LockpickingRepository.
 */
class SupabaseLockpickingRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : LockpickingRepository {

    override suspend fun createLockpicking(
        tableId: String,
        targetParticipantId: String,
        lockCount: Int,
        timeLimitSeconds: Int?,
        showLockCount: Boolean,
        soundEnabled: Boolean,
        createdBy: String
    ): Result<ActiveLockpicking> {
        return try {
            val insertDto = LockpickingInsertDto(
                tableId = tableId,
                targetParticipantId = targetParticipantId,
                lockCount = lockCount,
                timeLimitSeconds = timeLimitSeconds,
                showLockCount = showLockCount,
                soundEnabled = soundEnabled,
                unlockedCount = 0,
                status = "ACTIVE",
                createdBy = createdBy
            )

            val lockpicking = supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<ActiveLockpickingDto>()

            Result.success(lockpicking.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create lockpicking for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun unlockNext(
        lockpickingId: String,
        newUnlockedCount: Int,
        newStatus: LockpickingStatus
    ): Result<ActiveLockpicking> {
        return try {
            val updated = supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .update({
                    set("unlocked_count", newUnlockedCount)
                    set("status", newStatus.name)
                }) {
                    filter {
                        eq("id", lockpickingId)
                    }
                    select()
                }
                .decodeSingle<ActiveLockpickingDto>()

            Result.success(updated.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to unlock next for lockpicking=$lockpickingId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun cancel(
        lockpickingId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .update({
                    set("status", LockpickingStatus.CANCELLED.name)
                }) {
                    filter {
                        eq("id", lockpickingId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to cancel lockpicking=$lockpickingId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun timeout(
        lockpickingId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .update({
                    set("status", LockpickingStatus.TIMEOUT.name)
                }) {
                    filter {
                        eq("id", lockpickingId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to timeout lockpicking=$lockpickingId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveLockpickingForTable(
        tableId: String
    ): Result<ActiveLockpicking?> {
        return try {
            val result = supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("status", LockpickingStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveLockpickingDto>()
                .firstOrNull()

            Result.success(result?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active lockpicking for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveLockpickingForPlayer(
        tableId: String,
        playerId: String
    ): Result<ActiveLockpicking?> {
        return try {
            val result = supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("target_participant_id", playerId)
                        eq("status", LockpickingStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveLockpickingDto>()
                .firstOrNull()

            Result.success(result?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active lockpicking for player=$playerId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeLockpicking(
        lockpickingId: String
    ): Flow<ActiveLockpicking?> = observeLockpickingRow(
        description = "id=$lockpickingId",
        load = {
            supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .select(Columns.ALL) {
                    filter { eq("id", lockpickingId) }
                }
                .decodeList<ActiveLockpickingDto>()
                .firstOrNull()
        },
        concerns = { record -> extractString(record["id"]) == lockpickingId }
    )

    override fun observeActiveLockpickingForPlayer(
        tableId: String,
        playerId: String
    ): Flow<ActiveLockpicking?> = observeLockpickingRow(
        description = "player=$playerId",
        load = {
            supabaseClient
                .from(LOCKPICKINGS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("target_participant_id", playerId)
                        eq("status", LockpickingStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveLockpickingDto>()
                .firstOrNull()
        },
        concerns = { record ->
            extractString(record["target_participant_id"]) == playerId &&
                extractString(record["table_id"]) == tableId
        }
    )

    /**
     * Suit un crochetage : [load] à l'ouverture et après chaque coupure, puis chaque ligne reçue qui
     * le [concerns]. Un premier chargement raté émet `null` ; un rechargement raté garde l'état affiché.
     */
    private fun observeLockpickingRow(
        description: String,
        load: suspend () -> ActiveLockpickingDto?,
        concerns: (Map<String, Any?>) -> Boolean
    ): Flow<ActiveLockpicking?> = channelFlow {
        var loaded = false
        gameChannel.events(LOCKPICKINGS_TABLE).collect { event ->
            when (event) {
                GameChannelEvent.Ready -> try {
                    send(load()?.toDomain())
                    loaded = true
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    logInfrastructureFailure(TAG, "Failed to load lockpicking $description", e)
                    if (!loaded) send(null)
                }

                is GameChannelEvent.Change -> {
                    val record = event.change.record ?: return@collect
                    if (!concerns(record)) return@collect
                    val lockpicking = try {
                        parseLockpickingFromRecord(record)
                    } catch (e: Exception) {
                        AppLogger.error(TAG, "Failed to parse lockpicking update for $description", e)
                        return@collect
                    }
                    send(lockpicking)
                }
            }
        }
    }

    private fun parseLockpickingFromRecord(record: Map<String, Any?>): ActiveLockpicking? {
        val id = extractString(record["id"]) ?: return null
        val tableId = extractString(record["table_id"]) ?: return null
        val targetParticipantId = extractString(record["target_participant_id"]) ?: return null
        val lockCount = extractInt(record["lock_count"]) ?: return null
        val timeLimitSeconds = extractInt(record["time_limit_seconds"])
        val showLockCount = extractBoolean(record["show_lock_count"]) ?: false
        val soundEnabled = extractBoolean(record["sound_enabled"]) ?: true
        val unlockedCount = extractInt(record["unlocked_count"]) ?: 0
        val statusStr = extractString(record["status"]) ?: return null
        val status = try {
            LockpickingStatus.valueOf(statusStr)
        } catch (_: Exception) {
            return null
        }
        val createdBy = extractString(record["created_by"]) ?: return null
        val createdAtStr = extractString(record["created_at"]) ?: return null
        val createdAt = try {
            Instant.parse(createdAtStr)
        } catch (_: Exception) {
            Clock.System.now()
        }

        return ActiveLockpicking(
            id = id,
            tableId = tableId,
            targetParticipantId = targetParticipantId,
            lockCount = lockCount,
            timeLimitSeconds = timeLimitSeconds,
            showLockCount = showLockCount,
            soundEnabled = soundEnabled,
            unlockedCount = unlockedCount,
            status = status,
            createdBy = createdBy,
            createdAt = createdAt
        )
    }

    private fun extractBoolean(value: Any?): Boolean? = when (value) {
        null -> null
        is Boolean -> value
        is JsonPrimitive -> value.content.toBooleanStrictOrNull()
        is String -> value.toBooleanStrictOrNull()
        else -> null
    }

    private fun extractInt(value: Any?): Int? = when (value) {
        null -> null
        is Number -> value.toInt()
        is JsonPrimitive -> value.content.toIntOrNull()
        is String -> value.toIntOrNull()
        else -> null
    }

    private fun extractString(value: Any?): String? = when (value) {
        null -> null
        is JsonPrimitive -> value.content
        is String -> value
        else -> value.toString()
    }

    @Serializable
    private data class LockpickingInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("target_participant_id")
        val targetParticipantId: String,

        @SerialName("lock_count")
        val lockCount: Int,

        @SerialName("time_limit_seconds")
        val timeLimitSeconds: Int?,

        @SerialName("show_lock_count")
        val showLockCount: Boolean,

        @SerialName("sound_enabled")
        val soundEnabled: Boolean,

        @SerialName("unlocked_count")
        val unlockedCount: Int,

        @SerialName("status")
        val status: String,

        @SerialName("created_by")
        val createdBy: String
    )

    @Serializable
    private data class ActiveLockpickingDto(
        @SerialName("id")
        val id: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("target_participant_id")
        val targetParticipantId: String,

        @SerialName("lock_count")
        val lockCount: Int,

        @SerialName("time_limit_seconds")
        val timeLimitSeconds: Int?,

        @SerialName("show_lock_count")
        val showLockCount: Boolean,

        @SerialName("sound_enabled")
        val soundEnabled: Boolean,

        @SerialName("unlocked_count")
        val unlockedCount: Int,

        @SerialName("status")
        val status: String,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("created_at")
        val createdAt: Instant
    ) {
        fun toDomain(): ActiveLockpicking = ActiveLockpicking(
            id = id,
            tableId = tableId,
            targetParticipantId = targetParticipantId,
            lockCount = lockCount,
            timeLimitSeconds = timeLimitSeconds,
            showLockCount = showLockCount,
            soundEnabled = soundEnabled,
            unlockedCount = unlockedCount,
            status = LockpickingStatus.valueOf(status),
            createdBy = createdBy,
            createdAt = createdAt
        )
    }
}
